import { useState, useEffect, useCallback } from 'react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

// ─── heading ID generator (used by BlogPostPage) ───────────────────────────────
export function generateHeadingIds(content: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = content;

  const used = new Set<string>();
  temp.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
    const text = el.textContent?.trim() || '';
    let slug = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!slug) slug = 'heading';
    if (used.has(slug)) {
      let i = 2;
      while (used.has(`${slug}-${i}`)) i += 1;
      slug = `${slug}-${i}`;
    }
    used.add(slug);
    el.id = slug;
  });

  return temp.innerHTML;
}

// ─── sidebar component ────────────────────────────────────────────────────────

interface InThisPostProps {
  contentRef?: React.RefObject<HTMLElement | null>;
  selector?: string;
  contentHtml?: string;
}

export default function InThisPost({ contentRef, selector = '.prose-content', contentHtml }: InThisPostProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const extractHeadings = () => {
      let container: HTMLElement | null = null;
      if (contentRef?.current) {
        container = contentRef.current;
      } else if (contentHtml) {
        // Create a temporary off-screen container to parse the HTML
        const temp = document.createElement('div');
        temp.style.position = 'absolute';
        temp.style.left = '-9999px';
        temp.style.top = '0';
        temp.innerHTML = contentHtml;
        document.body.appendChild(temp);
        container = temp;
      } else {
        container = document.querySelector(selector) as HTMLElement | null;
      }
      if (!container) return;

      const hElements = container.querySelectorAll('h1[id], h2[id], h3[id]');
      const found: Heading[] = [];
      hElements.forEach((el) => {
        found.push({
          id: el.id,
          text: el.textContent?.trim() || '',
          level: parseInt(el.tagName[1], 10),
        });
      });
      setHeadings(found);

      // Clean up temp element if we created one
      if (contentHtml && container !== contentRef?.current) {
        document.body.removeChild(container);
      }
    };

    extractHeadings();
    const timer = setTimeout(extractHeadings, 500);
    return () => clearTimeout(timer);
  }, [contentRef, selector, contentHtml]);

  // IntersectionObserver to track active heading while scrolling
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        let best: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          }
        }
        if (best) {
          setActiveId(best.target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [headings]);

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const navbarHeight = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight - 16;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveId(id);
    }
  }, []);

  if (headings.length === 0) return null;

  return (
    <div className="bg-background-50">
      {/* Header */}
      <h3 className="text-sm font-medium text-foreground-700 flex items-center gap-2 mb-1">
        <span className="w-3.5 h-3.5 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="2" width="12" height="1.5" rx="0.75" fill="currentColor"/>
            <rect x="1" y="6.25" width="12" height="1.5" rx="0.75" fill="currentColor"/>
            <rect x="1" y="10.5" width="8" height="1.5" rx="0.75" fill="currentColor"/>
          </svg>
        </span>
        On this page
      </h3>

      {/* Nav list */}
      <nav className="mt-1">
        <ul className="space-y-0.5">
          {headings.map((heading) => {
            const isActive = activeId === heading.id;
            const indentClass =
              heading.level === 2 ? 'pl-4' :
              heading.level === 3 ? 'pl-7' : '';

            return (
              <li key={heading.id}>
                <button
                  onClick={() => handleClick(heading.id)}
                  className={`w-full text-left text-sm leading-snug py-1 transition-colors duration-150 cursor-pointer ${indentClass} ${
                    isActive
                      ? 'text-primary-500 font-semibold'
                      : heading.level === 1
                        ? 'text-foreground-700 hover:text-foreground-900 font-medium'
                        : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  {heading.text}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}